import { useCallback, useEffect, useState } from 'react';
import { Cloud } from 'lucide-react';
import { toast } from 'sonner';
import { adminSettingsService, type CloudVisionSetting as CloudVisionState } from '../../services/api';

/**
 * Settings: cloud vision for image intake (owner decision OD27).
 *
 * Admin tiers only (the parent renders this behind the `settings.cloud_vision` feature; the API enforces it).
 * Off by default. When on, the images uploaded through image intake are also sent to the named provider; the
 * warning text comes from the backend so the words printed here and recorded server-side are the same.
 */
export function CloudVisionSetting() {
    const [state, setState] = useState<CloudVisionState | null>(null);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);

    const load = useCallback(async () => {
        try {
            setState(await adminSettingsService.getCloudVision());
            setFailed(false);
        } catch {
            setFailed(true);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const toggle = async () => {
        if (!state || busy) return;
        setBusy(true);
        try {
            const next = await adminSettingsService.setCloudVision(!state.cloud_vision_enabled);
            setState(next);
            toast.success(next.cloud_vision_enabled
                ? 'Cloud vision is ON: images uploaded through image intake now leave this server.'
                : 'Cloud vision is OFF: images stay on this server.');
        } catch {
            toast.error('Could not change the setting. It was not changed.');
        } finally {
            setBusy(false);
        }
    };

    const on = !!state?.cloud_vision_enabled;
    const provider = state?.cloud_vision_provider === 'gemini' ? 'Google Gemini' : state?.cloud_vision_provider;

    return (
        <div className="bg-secondary/20 border border-border rounded-xl p-6" data-testid="cloud-vision-setting" data-tour="cloud-vision-setting">
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Cloud className="w-5 h-5 text-primary" /> Cloud vision for image intake
            </h2>
            {failed && <p className="text-sm text-destructive" role="alert">The setting could not be loaded.</p>}
            {state && (
                <div className="space-y-3">
                    <p className="text-sm text-muted-foreground" data-testid="cloud-vision-warning">{state.warning}</p>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            role="switch"
                            aria-checked={on}
                            aria-label="Cloud vision for image intake"
                            disabled={busy}
                            onClick={toggle}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full border border-border transition-colors disabled:opacity-50 ${on ? 'bg-destructive/70' : 'bg-secondary'}`}
                            data-testid="cloud-vision-toggle"
                        >
                            <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className="text-sm font-medium text-foreground" data-testid="cloud-vision-status">
                            {on ? `On: images go to ${provider}` : 'Off: images stay on this server'}
                        </span>
                    </div>
                    {on && !state.effective && (
                        <p className="text-xs text-amber-500" data-testid="cloud-vision-inactive">
                            {state.locked_by_deployment
                                ? 'On, but this deployment is locked: no image leaves the server.'
                                : !state.provider_key_configured
                                    ? `On, but no ${provider} key is configured on the server: no image leaves the server.`
                                    : 'On, but not active.'}
                        </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                        Provider {provider}, model {state.model}.
                        {state.updated_at ? ` Last changed ${new Date(state.updated_at).toLocaleString()}.` : ' Never changed (default off).'}
                        {' '}Officers can still exclude a single image on the Image card.
                    </p>
                </div>
            )}
        </div>
    );
}

export default CloudVisionSetting;
